/**
 * The global "create" shortcuts are Linear/Gmail-style key SEQUENCES: press
 * "c", then a second key naming what to create ("w" workshop, "b" booking,
 * "v" venue). A sequence sidesteps the browser-reserved modifier chords
 * (Ctrl/Cmd+W closes the window, etc.). The two keys must land within
 * SEQUENCE_WINDOW_MS of each other.
 */
import { useEffect, useRef } from 'react';

const SEQUENCE_WINDOW_MS = 1200;

/** True when the keyboard event comes from a text-entry field, so a shortcut
 *  never steals keystrokes mid-typing. */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/** True while the ⌘K palette is open — it has its own input and key handling.
 *  Read from the DOM so the shortcuts don't depend on the palette's state. */
function isPaletteOpen(): boolean {
  return (
    typeof document !== 'undefined' &&
    !!document.querySelector('[role="dialog"][aria-label="Command palette"]')
  );
}

/** Runs `onTrigger` when "c" is followed by `secondKey`. Unbound while `enabled` is false. */
export function useCreateShortcut(secondKey: string, enabled: boolean, onTrigger: () => void) {
  const cPressedAt = useRef(0);
  const trigger = useRef(onTrigger);
  trigger.current = onTrigger;

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target) || isPaletteOpen()) return;
      const key = e.key.toLowerCase();
      if (key === 'c') {
        cPressedAt.current = Date.now();
        return;
      }
      if (key === secondKey && Date.now() - cPressedAt.current <= SEQUENCE_WINDOW_MS) {
        cPressedAt.current = 0;
        e.preventDefault();
        trigger.current();
        return;
      }
      cPressedAt.current = 0;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, secondKey]);
}
